import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import {
  Boxes,
  ArrowRightLeft,
  ArrowUpRight,
  ArrowDownLeft,
  SlidersHorizontal,
  History,
  Search,
  Plus,
  Minus,
  Edit3,
  Trash2,
  Download,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Tag,
  MapPin,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  DollarSign,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { usePermissao } from '../../hooks/usePermissao';
import { useToast, useConfirm } from '../../Components/Feedback';
import { useApi } from '../../api/useApi';
import { Modal, Button } from '../../Components/ui';

function getTodayLocal() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const date = new Date(d.getTime() - offset * 60 * 1000);
  return date.toISOString().split('T')[0];
}

const CATEGORIAS_PADRAO = [
  'Material Escolar / Pedagógico',
  'Uniformes & Vestuário',
  'Alimentos & Lanche',
  'Limpeza & Higiene',
  'Escritório & Papelaria',
  'Equipamentos & Ferramentas',
  'Informática & Tecnologia',
  'Saúde & Primeiros Socorros',
  'Outros',
];

const UNIDADES_MEDIDA = [
  { value: 'UN', label: 'Unidade (UN)' },
  { value: 'CX', label: 'Caixa (CX)' },
  { value: 'PCT', label: 'Pacote (PCT)' },
  { value: 'PAR', label: 'Par (PAR)' },
  { value: 'KG', label: 'Quilo (KG)' },
  { value: 'G', label: 'Grama (G)' },
  { value: 'L', label: 'Litro (L)' },
  { value: 'ML', label: 'Mililitro (ML)' },
  { value: 'M', label: 'Metro (M)' },
  { value: 'KIT', label: 'Kit (KIT)' },
];

function formatarMoeda(valor) {
  if (valor === null || valor === undefined || isNaN(Number(valor))) return 'R$ 0,00';
  return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarDataHora(isoString) {
  if (!isoString) return '-';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

export default function Estoque() {
  const { institutionId } = useInstitution();
  const { podeCriar, podeEditar, podeExcluir, podeExportar } = usePermissao('/estoque');
  const toast = useToast();
  const confirm = useConfirm();
  const api = useApi();
  const queryClient = useQueryClient();

  // Filtros e ordenação
  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroLocalizacao, setFiltroLocalizacao] = useState('todas');
  const [ordenacao, setOrdenacao] = useState({ campo: 'nome', direcao: 'asc' });

  // Modais
  const [modalItemAberto, setModalItemAberto] = useState(false);
  const [itemEmEdicao, setItemEmEdicao] = useState(null); // null = criar, obj = editar
  const [formItem, setFormItem] = useState({
    codigo: '',
    nome: '',
    categoria: '',
    unidade_medida: 'UN',
    quantidade: '0',
    quantidade_minima: '0',
    quantidade_ideal: '',
    localizacao: '',
    preco_unitario: '',
    observacoes: '',
  });

  // Modal Movimentação
  const [modalMovimentacaoAberto, setModalMovimentacaoAberto] = useState(false);
  const [formMovimentacao, setFormMovimentacao] = useState({
    id_item: '',
    tipo: 'entrada', // 'entrada' | 'saida' | 'ajuste'
    quantidade: '1',
    motivo: '',
    destinatario: '',
    observacoes: '',
    data_movimentacao: getTodayLocal(),
  });

  // Modal Histórico
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);
  const [historicoFiltroItem, setHistoricoFiltroItem] = useState('');
  const [historicoFiltroTipo, setHistoricoFiltroTipo] = useState('todos');

  // Buscar dados principais
  const { data: respEstoque, isLoading } = useQuery({
    queryKey: ['estoque', institutionId],
    queryFn: async () => {
      return api.get('/api/estoque');
    },
    enabled: Boolean(institutionId),
  });

  const itens = useMemo(
    () => respEstoque?.data?.itens || respEstoque?.itens || [],
    [respEstoque],
  );
  const resumo = respEstoque?.data?.resumo || respEstoque?.resumo || {
    total_itens: 0,
    itens_normais: 0,
    itens_baixos: 0,
    itens_esgotados: 0,
    valor_total_estoque: 0,
    movimentacoes_mes: 0,
  };

  // Buscar histórico de movimentações (quando modal aberto)
  const { data: respHistorico, isLoading: isLoadingHistorico } = useQuery({
    queryKey: ['estoque-historico', institutionId, historicoFiltroItem, historicoFiltroTipo],
    queryFn: async () => {
      const query = new URLSearchParams();
      if (historicoFiltroItem) query.append('id_item', historicoFiltroItem);
      if (historicoFiltroTipo !== 'todos') query.append('tipo', historicoFiltroTipo);
      const url = `/api/estoque/historico${query.toString() ? `?${query.toString()}` : ''}`;
      return api.get(url);
    },
    enabled: Boolean(institutionId && modalHistoricoAberto),
  });

  const historicoMovimentacoes = Array.isArray(respHistorico?.data)
    ? respHistorico.data
    : Array.isArray(respHistorico)
      ? respHistorico
      : [];

  // Mutations
  const salvarItemMutation = useMutation({
    mutationFn: async (payload) => {
      if (itemEmEdicao) {
        return api.put(`/api/estoque/${itemEmEdicao.id}`, payload);
      }
      return api.post('/api/estoque', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estoque', institutionId] });
      toast.success(itemEmEdicao ? 'Item atualizado com sucesso!' : 'Item cadastrado com sucesso!');
      setModalItemAberto(false);
      setItemEmEdicao(null);
    },
    onError: (err) => {
      const msg =
        err.dados?.error ||
        err.dados?.message ||
        err.message ||
        'Erro ao salvar item.';
      toast.error(msg);
    },
  });

  const excluirItemMutation = useMutation({
    mutationFn: async (id) => {
      return api.del(`/api/estoque/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estoque', institutionId] });
      toast.success('Item removido com sucesso!');
    },
    onError: (err) => {
      const msg =
        err.dados?.error ||
        err.dados?.message ||
        err.message ||
        'Erro ao excluir item.';
      toast.error(msg);
    },
  });

  const movimentarMutation = useMutation({
    mutationFn: async (payload) => {
      return api.post('/api/estoque/movimentar', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estoque', institutionId] });
      queryClient.invalidateQueries({ queryKey: ['estoque-historico', institutionId] });
      toast.success('Movimentação registrada com sucesso!');
      setModalMovimentacaoAberto(false);
    },
    onError: (err) => {
      const msg =
        err.dados?.error ||
        err.dados?.message ||
        err.message ||
        'Erro ao registrar movimentação.';
      toast.error(msg);
    },
  });

  // Handlers para abrir modais
  const handleNovoItem = () => {
    setItemEmEdicao(null);
    setFormItem({
      codigo: '',
      nome: '',
      categoria: 'Material Escolar / Pedagógico',
      unidade_medida: 'UN',
      quantidade: '0',
      quantidade_minima: '0',
      quantidade_ideal: '',
      localizacao: '',
      preco_unitario: '',
      observacoes: '',
    });
    setModalItemAberto(true);
  };

  const handleEditarItem = (item) => {
    setItemEmEdicao(item);
    setFormItem({
      codigo: item.codigo || '',
      nome: item.nome || '',
      categoria: item.categoria || 'Geral',
      unidade_medida: item.unidade_medida || 'UN',
      quantidade: String(item.quantidade ?? item.quantidade_atual ?? 0),
      quantidade_minima: String(item.quantidade_minima ?? 0),
      quantidade_ideal:
        item.quantidade_ideal !== null && item.quantidade_ideal !== undefined
          ? String(item.quantidade_ideal)
          : '',
      localizacao: item.localizacao || '',
      preco_unitario:
        item.preco_unitario !== null && item.preco_unitario !== undefined
          ? String(item.preco_unitario)
          : item.custo_unitario !== null && item.custo_unitario !== undefined
            ? String(item.custo_unitario)
            : '',
      observacoes: item.observacoes || item.descricao || '',
    });
    setModalItemAberto(true);
  };

  const handleExcluirItem = async (item) => {
    const ok = await confirm(
      `Tem certeza que deseja excluir o item "${item.nome}"? O histórico de movimentações anteriores será preservado.`,
      {
        title: 'Excluir Item de Estoque',
        confirmText: 'Excluir Item',
        cancelText: 'Cancelar',
        tone: 'danger',
      },
    );
    if (ok) {
      excluirItemMutation.mutate(item.id);
    }
  };

  const handleAbrirMovimentacao = (itemPredefinido = null, tipoPredefinido = 'entrada') => {
    setFormMovimentacao({
      id_item: itemPredefinido ? String(itemPredefinido.id) : '',
      tipo: tipoPredefinido,
      quantidade: '1',
      motivo: '',
      destinatario: '',
      observacoes: '',
      data_movimentacao: getTodayLocal(),
    });
    setModalMovimentacaoAberto(true);
  };

  const handleAbrirHistorico = (item = null) => {
    setHistoricoFiltroItem(item ? String(item.id) : '');
    setHistoricoFiltroTipo('todos');
    setModalHistoricoAberto(true);
  };

  const handleSubmitItem = (e) => {
    e.preventDefault();
    if (!formItem.nome.trim()) {
      toast.warning('O nome do item é obrigatório.');
      return;
    }
    const payload = {
      ...formItem,
      nome: formItem.nome.trim(),
      codigo: formItem.codigo ? formItem.codigo.trim() : null,
      categoria:
        formItem.categoria && formItem.categoria.trim() ? formItem.categoria.trim() : 'Geral',
      unidade_medida: formItem.unidade_medida ? formItem.unidade_medida.trim() : 'UN',
      quantidade: Number(formItem.quantidade) || 0,
      quantidade_inicial: Number(formItem.quantidade) || 0,
      quantidade_minima: Number(formItem.quantidade_minima) || 0,
      quantidade_ideal:
        formItem.quantidade_ideal !== '' && formItem.quantidade_ideal !== null
          ? Number(formItem.quantidade_ideal)
          : null,
      preco_unitario:
        formItem.preco_unitario !== '' && formItem.preco_unitario !== null
          ? Number(formItem.preco_unitario)
          : 0,
      custo_unitario:
        formItem.preco_unitario !== '' && formItem.preco_unitario !== null
          ? Number(formItem.preco_unitario)
          : 0,
      observacoes: formItem.observacoes ? formItem.observacoes.trim() : '',
      descricao: formItem.observacoes ? formItem.observacoes.trim() : '',
    };
    salvarItemMutation.mutate(payload);
  };

  const handleSubmitMovimentacao = (e) => {
    e.preventDefault();
    if (!formMovimentacao.id_item) {
      toast.warning('Selecione o item para movimentar.');
      return;
    }
    const qtdNum = Number(formMovimentacao.quantidade);
    if (isNaN(qtdNum) || qtdNum <= 0) {
      toast.warning('Informe uma quantidade válida maior que zero.');
      return;
    }
    movimentarMutation.mutate(formMovimentacao);
  };

  // Alternar ordenação
  const alternarOrdenacao = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo) {
        return { campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo, direcao: 'asc' };
    });
  };

  // Categorias e localizações dinâmicas encontradas nos dados
  const categoriasDisponiveis = useMemo(() => {
    const setCat = new Set();
    CATEGORIAS_PADRAO.forEach((c) => setCat.add(c));
    itens.forEach((it) => {
      if (it.categoria) setCat.add(it.categoria);
    });
    return Array.from(setCat).sort();
  }, [itens]);

  const localizacoesDisponiveis = useMemo(() => {
    const setLoc = new Set();
    itens.forEach((it) => {
      if (it.localizacao && it.localizacao.trim()) setLoc.add(it.localizacao.trim());
    });
    return Array.from(setLoc).sort();
  }, [itens]);

  // Filtragem e ordenação da tabela
  const itensFiltrados = useMemo(() => {
    const filtrados = itens.filter((it) => {
      const matchBusca =
        !busca.trim() ||
        it.nome.toLowerCase().includes(busca.toLowerCase()) ||
        (it.codigo && it.codigo.toLowerCase().includes(busca.toLowerCase())) ||
        (it.categoria && it.categoria.toLowerCase().includes(busca.toLowerCase())) ||
        (it.localizacao && it.localizacao.toLowerCase().includes(busca.toLowerCase()));

      const matchCat = filtroCategoria === 'todas' || it.categoria === filtroCategoria;
      const matchStatus = filtroStatus === 'todos' || it.status_estoque === filtroStatus;
      const matchLoc = filtroLocalizacao === 'todas' || it.localizacao === filtroLocalizacao;

      return matchBusca && matchCat && matchStatus && matchLoc;
    });

    const { campo, direcao } = ordenacao;
    const mult = direcao === 'asc' ? 1 : -1;

    const PESO_STATUS = {
      esgotado: 3,
      baixo: 2,
      normal: 1,
    };

    return [...filtrados].sort((a, b) => {
      if (campo === 'nome') {
        return mult * (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
      }
      if (campo === 'codigo') {
        return mult * (a.codigo || '').localeCompare(b.codigo || '', 'pt-BR');
      }
      if (campo === 'categoria') {
        return mult * (a.categoria || '').localeCompare(b.categoria || '', 'pt-BR');
      }
      if (campo === 'localizacao') {
        return mult * (a.localizacao || '').localeCompare(b.localizacao || '', 'pt-BR');
      }
      if (campo === 'quantidade') {
        return mult * (Number(a.quantidade || 0) - Number(b.quantidade || 0));
      }
      if (campo === 'quantidade_minima') {
        return mult * (Number(a.quantidade_minima || 0) - Number(b.quantidade_minima || 0));
      }
      if (campo === 'status') {
        return mult * ((PESO_STATUS[a.status_estoque] || 0) - (PESO_STATUS[b.status_estoque] || 0));
      }
      if (campo === 'valor_total') {
        const totalA = Number(a.quantidade || 0) * Number(a.preco_unitario || 0);
        const totalB = Number(b.quantidade || 0) * Number(b.preco_unitario || 0);
        return mult * (totalA - totalB);
      }
      return 0;
    });
  }, [itens, busca, filtroCategoria, filtroStatus, filtroLocalizacao, ordenacao]);

  // Exportar Excel
  const handleExportarExcel = () => {
    if (itensFiltrados.length === 0) {
      toast.warning('Nenhum item para exportar com os filtros atuais.');
      return;
    }

    const dados = itensFiltrados.map((it) => ({
      Código: it.codigo || '',
      Item: it.nome,
      Categoria: it.categoria || '',
      Unidade: it.unidade_medida || 'UN',
      'Estoque Atual': Number(it.quantidade || 0),
      'Estoque Mínimo': Number(it.quantidade_minima || 0),
      'Estoque Ideal': it.quantidade_ideal !== null ? Number(it.quantidade_ideal) : '',
      Status:
        it.status_estoque === 'esgotado'
          ? 'Esgotado'
          : it.status_estoque === 'baixo'
            ? 'Estoque Baixo'
            : 'Normal',
      Localização: it.localizacao || '',
      'Preço Unitário (R$)': it.preco_unitario !== null ? Number(it.preco_unitario) : 0,
      'Valor Total em Estoque (R$)':
        Number(it.quantidade || 0) * (it.preco_unitario !== null ? Number(it.preco_unitario) : 0),
      Observações: it.observacoes || '',
    }));

    const ws = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Estoque');
    const hoje = getTodayLocal();
    XLSX.writeFile(wb, `relatorio_estoque_${hoje}.xlsx`);
    toast.success('Relatório exportado com sucesso!');
  };

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              Gerenciamento de Estoque
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              {itens.length} {itens.length === 1 ? 'item' : 'itens'}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Controle de materiais, uniformes, suprimentos, entradas, saídas e ponto de ressuprimento.
          </p>
        </div>

        {/* Ações principais do topo */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={History}
            onClick={() => handleAbrirHistorico()}
            title="Ver histórico de entradas, saídas e ajustes"
          >
            Histórico
          </Button>

          {podeExportar && (
            <Button
              variant="secondary"
              size="sm"
              icon={Download}
              onClick={handleExportarExcel}
              title="Exportar para Excel (XLSX)"
            >
              Exportar
            </Button>
          )}

          {podeCriar && (
            <>
              <Button
                variant="secondary"
                size="sm"
                icon={ArrowRightLeft}
                onClick={() => handleAbrirMovimentacao()}
                title="Registrar entrada, saída ou ajuste de saldo"
              >
                Movimentar
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={Plus}
                onClick={handleNovoItem}
                title="Cadastrar novo item no estoque"
              >
                Novo Item
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Cards de Métricas e Resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {/* Card Total */}
        <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Total de Itens
            </span>
            <div className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400">
              <Boxes className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              {resumo.total_itens}
            </span>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
              Valor: {formatarMoeda(resumo.valor_total_estoque)}
            </p>
          </div>
        </div>

        {/* Card Normal */}
        <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Estoque Normal
            </span>
            <div className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
              {resumo.itens_normais}
            </span>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Nível suficiente</p>
          </div>
        </div>

        {/* Card Estoque Baixo */}
        <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Estoque Baixo
            </span>
            <div className="p-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-xl font-bold text-amber-600 dark:text-amber-400">
              {resumo.itens_baixos}
            </span>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Requer compra</p>
          </div>
        </div>

        {/* Card Esgotados */}
        <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Esgotados
            </span>
            <div className="p-1.5 rounded-lg bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400">
              <XCircle className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-xl font-bold text-rose-600 dark:text-rose-400">
              {resumo.itens_esgotados}
            </span>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Saldo zerado</p>
          </div>
        </div>

        {/* Card Movimentações no Mês */}
        <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between col-span-2 sm:col-span-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Movim. no Mês
            </span>
            <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
              <ArrowRightLeft className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
              {resumo.movimentacoes_mes}
            </span>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Entradas e saídas</p>
          </div>
        </div>
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="p-3 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col md:flex-row gap-2.5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por código, nome do item, categoria ou localização..."
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Filtro Categoria */}
          <select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
            className="px-2.5 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="todas">Categoria: Todas</option>
            {categoriasDisponiveis.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          {/* Filtro Status */}
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-2.5 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="todos">Status: Todos</option>
            <option value="normal">Normal / Disponível</option>
            <option value="baixo">Estoque Baixo</option>
            <option value="esgotado">Esgotado</option>
          </select>

          {/* Filtro Localização */}
          {localizacoesDisponiveis.length > 0 && (
            <select
              value={filtroLocalizacao}
              onChange={(e) => setFiltroLocalizacao(e.target.value)}
              className="px-2.5 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="todas">Localização: Todas</option>
              {localizacoesDisponiveis.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          )}

          {(busca ||
            filtroCategoria !== 'todas' ||
            filtroStatus !== 'todos' ||
            filtroLocalizacao !== 'todas') && (
            <button
              onClick={() => {
                setBusca('');
                setFiltroCategoria('todas');
                setFiltroStatus('todos');
                setFiltroLocalizacao('todas');
              }}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline px-1"
            >
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* Tabela de Itens de Estoque */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-400">
            <Loader2 className="w-7 h-7 animate-spin text-blue-500 mb-2" />
            <p className="text-xs">Carregando itens de estoque...</p>
          </div>
        ) : itensFiltrados.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Boxes className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Nenhum item de estoque encontrado
            </p>
            <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
              {busca ||
              filtroCategoria !== 'todas' ||
              filtroStatus !== 'todos' ||
              filtroLocalizacao !== 'todas'
                ? 'Nenhum item corresponde aos filtros aplicados.'
                : 'Cadastre materiais, produtos e uniformes para iniciar o controle de estoque.'}
            </p>
            {podeCriar && !busca && (
              <div className="mt-4">
                <Button variant="primary" size="sm" icon={Plus} onClick={handleNovoItem}>
                  Cadastrar Primeiro Item
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 text-[11px] uppercase font-semibold whitespace-nowrap select-none">
                  {/* Item / Produto */}
                  <th
                    onClick={() => alternarOrdenacao('nome')}
                    className="py-2 px-3 cursor-pointer hover:bg-gray-100/70 dark:hover:bg-gray-700/50 transition-colors"
                    title="Clique para ordenar por item (A-Z / Z-A)"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Boxes className="w-3.5 h-3.5 text-gray-400" />
                      <span
                        className={
                          ordenacao.campo === 'nome' ? 'text-gray-900 dark:text-white font-bold' : ''
                        }
                      >
                        Item / Código
                      </span>
                      {ordenacao.campo === 'nome' ? (
                        ordenacao.direcao === 'asc' ? (
                          <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-gray-300 dark:text-gray-600" />
                      )}
                    </span>
                  </th>

                  {/* Categoria */}
                  <th
                    onClick={() => alternarOrdenacao('categoria')}
                    className="py-2 px-3 cursor-pointer hover:bg-gray-100/70 dark:hover:bg-gray-700/50 transition-colors"
                    title="Clique para ordenar por categoria"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5 text-gray-400" />
                      <span
                        className={
                          ordenacao.campo === 'categoria'
                            ? 'text-gray-900 dark:text-white font-bold'
                            : ''
                        }
                      >
                        Categoria
                      </span>
                      {ordenacao.campo === 'categoria' ? (
                        ordenacao.direcao === 'asc' ? (
                          <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-gray-300 dark:text-gray-600" />
                      )}
                    </span>
                  </th>

                  {/* Localização */}
                  <th
                    onClick={() => alternarOrdenacao('localizacao')}
                    className="py-2 px-3 cursor-pointer hover:bg-gray-100/70 dark:hover:bg-gray-700/50 transition-colors"
                    title="Clique para ordenar por localização"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                      <span
                        className={
                          ordenacao.campo === 'localizacao'
                            ? 'text-gray-900 dark:text-white font-bold'
                            : ''
                        }
                      >
                        Localização
                      </span>
                      {ordenacao.campo === 'localizacao' ? (
                        ordenacao.direcao === 'asc' ? (
                          <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-gray-300 dark:text-gray-600" />
                      )}
                    </span>
                  </th>

                  {/* Estoque Atual */}
                  <th
                    onClick={() => alternarOrdenacao('quantidade')}
                    className="py-2 px-3 cursor-pointer hover:bg-gray-100/70 dark:hover:bg-gray-700/50 transition-colors text-right"
                    title="Clique para ordenar por quantidade em estoque"
                  >
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      <span
                        className={
                          ordenacao.campo === 'quantidade'
                            ? 'text-gray-900 dark:text-white font-bold'
                            : ''
                        }
                      >
                        Estoque Atual
                      </span>
                      {ordenacao.campo === 'quantidade' ? (
                        ordenacao.direcao === 'asc' ? (
                          <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-gray-300 dark:text-gray-600" />
                      )}
                    </span>
                  </th>

                  {/* Mínimo / Ideal */}
                  <th
                    onClick={() => alternarOrdenacao('quantidade_minima')}
                    className="py-2 px-3 cursor-pointer hover:bg-gray-100/70 dark:hover:bg-gray-700/50 transition-colors text-right"
                    title="Clique para ordenar por estoque mínimo"
                  >
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      <span
                        className={
                          ordenacao.campo === 'quantidade_minima'
                            ? 'text-gray-900 dark:text-white font-bold'
                            : ''
                        }
                      >
                        Mín. / Ideal
                      </span>
                      {ordenacao.campo === 'quantidade_minima' ? (
                        ordenacao.direcao === 'asc' ? (
                          <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-gray-300 dark:text-gray-600" />
                      )}
                    </span>
                  </th>

                  {/* Valor Unit. / Total */}
                  <th
                    onClick={() => alternarOrdenacao('valor_total')}
                    className="py-2 px-3 cursor-pointer hover:bg-gray-100/70 dark:hover:bg-gray-700/50 transition-colors text-right"
                    title="Clique para ordenar por valor financeiro"
                  >
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      <DollarSign className="w-3.5 h-3.5 text-gray-400" />
                      <span
                        className={
                          ordenacao.campo === 'valor_total'
                            ? 'text-gray-900 dark:text-white font-bold'
                            : ''
                        }
                      >
                        Valor Total
                      </span>
                      {ordenacao.campo === 'valor_total' ? (
                        ordenacao.direcao === 'asc' ? (
                          <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-gray-300 dark:text-gray-600" />
                      )}
                    </span>
                  </th>

                  {/* Status */}
                  <th
                    onClick={() => alternarOrdenacao('status')}
                    className="py-2 px-3 cursor-pointer hover:bg-gray-100/70 dark:hover:bg-gray-700/50 transition-colors"
                    title="Clique para ordenar por status"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className={
                          ordenacao.campo === 'status'
                            ? 'text-gray-900 dark:text-white font-bold'
                            : ''
                        }
                      >
                        Status
                      </span>
                      {ordenacao.campo === 'status' ? (
                        ordenacao.direcao === 'asc' ? (
                          <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-gray-300 dark:text-gray-600" />
                      )}
                    </span>
                  </th>

                  {/* Ações */}
                  <th className="py-2 px-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {itensFiltrados.map((item) => {
                  const qtd = Number(item.quantidade ?? item.quantidade_atual ?? 0);
                  const min = Number(item.quantidade_minima ?? 0);
                  const preco = Number(item.preco_unitario ?? item.custo_unitario ?? 0);
                  const valorTotal = Number(item.valor_total ?? qtd * preco);

                  return (
                    <tr
                      key={item.id}
                      className="border-b border-gray-50 dark:border-gray-800/60 hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors"
                    >
                      {/* Item / Código */}
                      <td className="py-2 px-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 text-gray-600 dark:text-gray-300">
                            <Boxes className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white truncate max-w-[200px] sm:max-w-[240px]">
                              {item.nome}
                            </div>
                            <div className="text-[11px] text-gray-400 font-mono">
                              {item.codigo ? `#${item.codigo}` : 'S/ CÓDIGO'}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Categoria */}
                      <td className="py-2 px-3 whitespace-nowrap">
                        <span className="text-gray-600 dark:text-gray-300">
                          {item.categoria || '-'}
                        </span>
                      </td>

                      {/* Localização */}
                      <td className="py-2 px-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          {item.localizacao || 'Geral'}
                        </span>
                      </td>

                      {/* Estoque Atual */}
                      <td className="py-2 px-3 whitespace-nowrap text-right">
                        <span
                          className={`font-bold font-mono text-[13px] ${
                            item.status_estoque === 'esgotado'
                              ? 'text-rose-600 dark:text-rose-400'
                              : item.status_estoque === 'baixo'
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-gray-900 dark:text-white'
                          }`}
                        >
                          {qtd}
                        </span>{' '}
                        <span className="text-[11px] text-gray-400 uppercase font-medium">
                          {item.unidade_medida || 'UN'}
                        </span>
                      </td>

                      {/* Mínimo / Ideal */}
                      <td className="py-2 px-3 whitespace-nowrap text-right">
                        <span className="text-gray-500 dark:text-gray-400">
                          {min}{' '}
                          <span className="text-gray-300 dark:text-gray-600">/</span>{' '}
                          {item.quantidade_ideal !== null ? item.quantidade_ideal : '-'}
                        </span>
                      </td>

                      {/* Valor Unit. / Total */}
                      <td className="py-2 px-3 whitespace-nowrap text-right">
                        <div className="text-gray-900 dark:text-white font-medium">
                          {formatarMoeda(valorTotal)}
                        </div>
                        {preco > 0 && (
                          <div className="text-[10px] text-gray-400">
                            Un: {formatarMoeda(preco)}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-2 px-3 whitespace-nowrap">
                        {item.status_estoque === 'esgotado' ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                            Esgotado
                          </span>
                        ) : item.status_estoque === 'baixo' ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Estoque Baixo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Disponível
                          </span>
                        )}
                      </td>

                      {/* Ações */}
                      <td className="py-2 px-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          {podeCriar && (
                            <>
                              {/* Entrada rápida */}
                              <button
                                type="button"
                                onClick={() => handleAbrirMovimentacao(item, 'entrada')}
                                title="Entrada de estoque (+)"
                                className="w-7 h-7 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center transition-colors"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                              {/* Saída rápida */}
                              <button
                                type="button"
                                onClick={() => handleAbrirMovimentacao(item, 'saida')}
                                title="Saída de estoque (-)"
                                className="w-7 h-7 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/50 text-rose-600 dark:text-rose-400 flex items-center justify-center transition-colors"
                              >
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}

                          {/* Ver Histórico */}
                          <button
                            type="button"
                            onClick={() => handleAbrirHistorico(item)}
                            title="Extrato e histórico deste item"
                            className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center justify-center transition-colors"
                          >
                            <History className="w-3.5 h-3.5" />
                          </button>

                          {/* Editar */}
                          {podeEditar && (
                            <button
                              type="button"
                              onClick={() => handleEditarItem(item)}
                              title="Editar cadastro do item"
                              className="w-7 h-7 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center transition-colors"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {/* Excluir */}
                          {podeExcluir && (
                            <button
                              type="button"
                              onClick={() => handleExcluirItem(item)}
                              title="Excluir item"
                              className="w-7 h-7 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/50 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 flex items-center justify-center transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Cadastrar / Editar Item */}
      <Modal
        isOpen={modalItemAberto}
        onClose={() => setModalItemAberto(false)}
        title={itemEmEdicao ? 'Editar Item de Estoque' : 'Novo Item de Estoque'}
        size="lg"
      >
        <form onSubmit={handleSubmitItem} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Código / SKU */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Código / SKU (opcional)
              </label>
              <input
                type="text"
                value={formItem.codigo}
                onChange={(e) => setFormItem({ ...formItem, codigo: e.target.value })}
                placeholder="Ex: UNIF-CAM-G"
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* Nome do Item */}
            <div className="sm:col-span-2">
              <label htmlFor="nome-item" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome do Item <span className="text-rose-500">*</span>
              </label>
              <input
                id="nome-item"
                type="text"
                required
                value={formItem.nome}
                onChange={(e) => setFormItem({ ...formItem, nome: e.target.value })}
                placeholder="Ex: Camiseta do Projeto - Tamanho 14"
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Categoria */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Categoria
              </label>
              <input
                type="text"
                list="lista-categorias-sugestao"
                value={formItem.categoria}
                onChange={(e) => setFormItem({ ...formItem, categoria: e.target.value })}
                placeholder="Selecione ou digite..."
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              <datalist id="lista-categorias-sugestao">
                {CATEGORIAS_PADRAO.map((cat) => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
            </div>

            {/* Unidade de Medida */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Unidade de Medida
              </label>
              <select
                value={formItem.unidade_medida}
                onChange={(e) => setFormItem({ ...formItem, unidade_medida: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                {UNIDADES_MEDIDA.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Localização */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Localização / Depósito
              </label>
              <input
                type="text"
                value={formItem.localizacao}
                onChange={(e) => setFormItem({ ...formItem, localizacao: e.target.value })}
                placeholder="Ex: Almoxarifado - Prateleira B"
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/40 rounded-xl border border-gray-100 dark:border-gray-800">
            {/* Se for criação, permite definir quantidade inicial */}
            {!itemEmEdicao ? (
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Estoque Inicial
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={formItem.quantidade}
                  onChange={(e) => setFormItem({ ...formItem, quantidade: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Estoque Atual
                </label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl text-xs font-bold text-gray-700 dark:text-gray-300">
                  {formItem.quantidade} {formItem.unidade_medida}
                </div>
                <span className="text-[10px] text-gray-400">Ajuste via Movimentação</span>
              </div>
            )}

            {/* Quantidade Mínima */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Estoque Mínimo (Alerta)
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={formItem.quantidade_minima}
                onChange={(e) => setFormItem({ ...formItem, quantidade_minima: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* Quantidade Ideal */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Estoque Ideal / Máx.
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={formItem.quantidade_ideal}
                onChange={(e) => setFormItem({ ...formItem, quantidade_ideal: e.target.value })}
                placeholder="Opcional"
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* Preço Unitário */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Custo Unitário (R$)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formItem.preco_unitario}
                onChange={(e) => setFormItem({ ...formItem, preco_unitario: e.target.value })}
                placeholder="0.00"
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Observações / Informações Adicionais
            </label>
            <textarea
              rows={2}
              value={formItem.observacoes}
              onChange={(e) => setFormItem({ ...formItem, observacoes: e.target.value })}
              placeholder="Detalhes sobre fornecedor, especificações técnicas, lotes..."
              className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setModalItemAberto(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={salvarItemMutation.isPending}
            >
              {itemEmEdicao ? 'Salvar Alterações' : 'Cadastrar Item'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal: Registrar Movimentação de Estoque */}
      <Modal
        isOpen={modalMovimentacaoAberto}
        onClose={() => setModalMovimentacaoAberto(false)}
        title="Movimentação de Estoque"
        size="md"
      >
        <form onSubmit={handleSubmitMovimentacao} className="space-y-4">
          {/* Tipo de Movimentação */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Tipo de Movimentação
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setFormMovimentacao({ ...formMovimentacao, tipo: 'entrada' })}
                className={`py-2 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  formMovimentacao.tipo === 'entrada'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-500 shadow-sm'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50'
                }`}
              >
                <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-500" />
                Entrada (+)
              </button>

              <button
                type="button"
                onClick={() => setFormMovimentacao({ ...formMovimentacao, tipo: 'saida' })}
                className={`py-2 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  formMovimentacao.tipo === 'saida'
                    ? 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-500 shadow-sm'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50'
                }`}
              >
                <ArrowUpRight className="w-3.5 h-3.5 text-rose-500" />
                Saída (-)
              </button>

              <button
                type="button"
                onClick={() => setFormMovimentacao({ ...formMovimentacao, tipo: 'ajuste' })}
                className={`py-2 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  formMovimentacao.tipo === 'ajuste'
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-500 shadow-sm'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50'
                }`}
              >
                <SlidersHorizontal className="w-3.5 h-3.5 text-blue-500" />
                Ajuste / Balanço
              </button>
            </div>
          </div>

          {/* Selecionar Item */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Item / Produto <span className="text-rose-500">*</span>
            </label>
            <select
              required
              value={formMovimentacao.id_item}
              onChange={(e) =>
                setFormMovimentacao({ ...formMovimentacao, id_item: e.target.value })
              }
              className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">Selecione um item...</option>
              {itens.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.nome} {it.codigo ? `(#${it.codigo})` : ''} - Saldo: {it.quantidade}{' '}
                  {it.unidade_medida}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Quantidade */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {formMovimentacao.tipo === 'ajuste'
                  ? 'Novo Saldo Real'
                  : 'Quantidade a Movimentar'}{' '}
                <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="any"
                required
                value={formMovimentacao.quantidade}
                onChange={(e) =>
                  setFormMovimentacao({ ...formMovimentacao, quantidade: e.target.value })
                }
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* Data da Movimentação */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data do Registro
              </label>
              <input
                type="date"
                required
                value={formMovimentacao.data_movimentacao}
                onChange={(e) =>
                  setFormMovimentacao({ ...formMovimentacao, data_movimentacao: e.target.value })
                }
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Motivo */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motivo / Justificativa
              </label>
              <input
                type="text"
                list="lista-motivos-sugestao"
                value={formMovimentacao.motivo}
                onChange={(e) =>
                  setFormMovimentacao({ ...formMovimentacao, motivo: e.target.value })
                }
                placeholder="Ex: Compra, Entrega para aluno, Avaria..."
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              <datalist id="lista-motivos-sugestao">
                <option value="Compra / Aquisição" />
                <option value="Doação Recebida" />
                <option value="Entrega de Uniforme a Aluno" />
                <option value="Material de Sala de Aula" />
                <option value="Consumo Interno" />
                <option value="Devolução de Aluno" />
                <option value="Avaria / Estrago / Perda" />
                <option value="Acerto de Contagem / Balanço" />
              </datalist>
            </div>

            {/* Destinatário / Solicitante */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Destinatário / Solicitante (opcional)
              </label>
              <input
                type="text"
                value={formMovimentacao.destinatario}
                onChange={(e) =>
                  setFormMovimentacao({ ...formMovimentacao, destinatario: e.target.value })
                }
                placeholder="Nome do aluno, educador ou setor..."
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Observações
            </label>
            <input
              type="text"
              value={formMovimentacao.observacoes}
              onChange={(e) =>
                setFormMovimentacao({ ...formMovimentacao, observacoes: e.target.value })
              }
              placeholder="Ex: Nota fiscal #1234, turma matutino..."
              className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setModalMovimentacaoAberto(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={movimentarMutation.isPending}
            >
              Confirmar Movimentação
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal: Histórico de Movimentações */}
      <Modal
        isOpen={modalHistoricoAberto}
        onClose={() => setModalHistoricoAberto(false)}
        title="Histórico de Movimentações de Estoque"
        size="xl"
      >
        <div className="space-y-3">
          {/* Barra de Filtros do Histórico */}
          <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-50/70 dark:bg-gray-800/40 rounded-xl border border-gray-100 dark:border-gray-800">
            {/* Filtro Item */}
            <select
              value={historicoFiltroItem}
              onChange={(e) => setHistoricoFiltroItem(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 focus:outline-none"
            >
              <option value="">Todos os Itens</option>
              {itens.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.nome}
                </option>
              ))}
            </select>

            {/* Filtro Tipo */}
            <select
              value={historicoFiltroTipo}
              onChange={(e) => setHistoricoFiltroTipo(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 focus:outline-none"
            >
              <option value="todos">Todos os Tipos</option>
              <option value="entrada">Apenas Entradas</option>
              <option value="saida">Apenas Saídas</option>
              <option value="ajuste">Apenas Ajustes</option>
            </select>

            <span className="text-[11px] text-gray-400 ml-auto">
              Total de registros: {historicoMovimentacoes.length}
            </span>
          </div>

          {/* Tabela do Histórico */}
          <div className="max-h-[60vh] overflow-y-auto overflow-x-auto border border-gray-100 dark:border-gray-800 rounded-xl">
            {isLoadingHistorico ? (
              <div className="flex items-center justify-center p-8 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
                <span className="text-xs">Carregando movimentações...</span>
              </div>
            ) : historicoMovimentacoes.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <History className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-xs font-medium">Nenhuma movimentação registrada.</p>
              </div>
            ) : (
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 text-[11px] uppercase font-semibold whitespace-nowrap sticky top-0">
                    <th className="py-2 px-3">Data / Hora</th>
                    <th className="py-2 px-3">Item</th>
                    <th className="py-2 px-3">Tipo</th>
                    <th className="py-2 px-3 text-right">Qtd</th>
                    <th className="py-2 px-3 text-right">Saldo</th>
                    <th className="py-2 px-3">Motivo / Destinatário</th>
                    <th className="py-2 px-3">Registrado Por</th>
                  </tr>
                </thead>
                <tbody>
                  {historicoMovimentacoes.map((mov) => (
                    <tr
                      key={mov.id}
                      className="border-b border-gray-50 dark:border-gray-800/60 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                    >
                      <td className="py-2 px-3 whitespace-nowrap font-mono text-[11px] text-gray-500 dark:text-gray-400">
                        {formatarDataHora(mov.data_movimentacao || mov.criado_em)}
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                        {mov.item_nome || '-'}
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap">
                        {mov.tipo === 'entrada' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                            <ArrowDownLeft className="w-3 h-3 text-emerald-500" />
                            Entrada
                          </span>
                        ) : mov.tipo === 'saida' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
                            <ArrowUpRight className="w-3 h-3 text-rose-500" />
                            Saída
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                            <SlidersHorizontal className="w-3 h-3 text-blue-500" />
                            Ajuste
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap text-right font-bold font-mono">
                        {mov.tipo === 'saida' ? `-${mov.quantidade}` : `+${mov.quantidade}`}
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap text-right text-gray-500 dark:text-gray-400 font-mono text-[11px]">
                        {mov.saldo_anterior ?? mov.quantidade_anterior ?? 0}{' '}
                        <span className="text-gray-300">→</span>{' '}
                        <strong className="text-gray-900 dark:text-white">
                          {mov.saldo_novo ?? mov.quantidade_posterior ?? 0}
                        </strong>
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                        <div>{mov.motivo || '-'}</div>
                        {mov.destinatario && (
                          <div className="text-[10px] text-gray-400">
                            Dest: {mov.destinatario}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap text-gray-500 dark:text-gray-400 text-[11px]">
                        {mov.usuario_nome || mov.registrado_por_nome || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

